import React, { useEffect } from 'react';
import { useQuery, useReactiveVar } from '@apollo/client';
import PageInstructions from '../../shared/components/PageInstructions';
import GET_CHECK_POINTS from '../queries/getCheckPoints';
import AppraisalCards from './AppraisalCards';
import { useLocation, Navigate } from 'react-router-dom';
import { Page } from '../../layout';
import ApolloError from '../../shared/components/ApolloError';
import SuccessMessage from '../../shared/components/SuccessMessage';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import useOrgUnitIdFromURL from '../../shared/hooks/useOrgUnitIdFromURL';
import GET_ORG_UNIT_INFO from '../../shared/queries/getOrgUnitInfoQuery';
import { userTier } from '../../../apollo/cache';
import { getDataNotFoundPage } from '../../shared/services/getDataNotFoundPageService';
import { canUserAccessSeveralOrganization } from '../../shared/services/userAccessibilityService';
import OrgUnitBreadcrumbAnnotation from '../../shared/components/breadcrumb/annotations/OrgUnitBreadcrumbAnnotation';
import { getPageTitle } from '../../shared/services/pageTitleService';
import { checkPointData, initialCheckPointData } from '../checkPointsCache';
import { useCacheNetworkQuery } from '@apollo-internal/hooks/useCacheNetworkQuery';

const CheckPointsListingPage = () => {
  const orgUnitId = useOrgUnitIdFromURL();
  const location = useLocation();

  const tier = useReactiveVar(userTier);

  const description =
    'View all available check points for each appraisal below. ' +
    'Select a check point date to see more information. Click the "Add Check Point" button to submit new data. ';
  const breadcrumbPathItems = [{ name: 'Check Points', url: '' }];

  const {
    data: orgUnitData,
    error: orgUnitError,
    loading: orgUnitLoading
  } = useQuery(GET_ORG_UNIT_INFO, {
    variables: { id: orgUnitId }
  });

  const orgUnitName = orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.Name;
  const orgName =
    orgUnitData && orgUnitData.mddapOrgUnit && orgUnitData.mddapOrgUnit.MddapOrganization.Name;
  const renderedOrgUnit = orgUnitData && (
    <OrgUnitBreadcrumbAnnotation orgUnit={orgUnitData.mddapOrgUnit} key={orgUnitName} />
  );

  const pageTitle = getPageTitle([orgUnitName, 'Check Points']);
  const annotationItems = canUserAccessSeveralOrganization(tier)
    ? [orgName, renderedOrgUnit]
    : [renderedOrgUnit];

  const { data, error, loading } = useCacheNetworkQuery(GET_CHECK_POINTS, {
    variables: { orgUnitId: orgUnitId }
  });

  useEffect(() => {
    checkPointData(initialCheckPointData);
  }, []);

  const appraisalOrgUnits = data ? data.checkPoints.AppraisalOrgUnits : [];

  if (orgUnitData && orgUnitData.mddapOrgUnit === null) {
    return <Navigate to={getDataNotFoundPage(tier)} />;
  }

  if (error || orgUnitError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='check-points-listing'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={orgUnitLoading}
      />
      <PageInstructions title='Check Points' description={description} />
      {location.state && location.state.checkPointModifiedSuccessfullyMessage && (
        <SuccessMessage message={location.state.checkPointModifiedSuccessfullyMessage} />
      )}
      <AppraisalCards
        orgUnitId={orgUnitId}
        appraisalOrgUnits={appraisalOrgUnits}
        loading={loading}
      />
    </Page>
  );
};

export default CheckPointsListingPage;
